import { Flex, Text, Button } from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import cn from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { PageMeta } from "src/components";
import { stripMarkdown } from "src/components/PageMeta/stripMarkdown";
import { appConfig } from "src/config";
import { T } from "src/i18n";
import { RoutePathKeys } from "src/pages/KnowledgeBasePage/constants";
import { RoutesPaths } from "src/routes/RoutesPaths";
import { Footer } from "src/widgets/Footer/Footer";
import { Header } from "src/widgets/Header/Header";

import {
  KnowledgeSidebar,
  KnowledgeDropdownToc,
  KnowledgeMarkdown,
  KnowledgeToc,
} from "./components";
import styles from "./KnowledgeBaseWidget.module.scss";
import { knowledgePage, knowledgeTree } from "./store";
import { getKBTocItems } from "./utils";
export const KnowledgeBaseWidget = ({
  basePath,
  route,
}: {
  basePath: string;
  route: RoutePathKeys;
}) => {
  const navigate = useNavigate();
  const { "*": slugPath = "" } = useParams();
  const location = useLocation();
  const [activeId, setActiveId] = useState<null | string>(null);
  const loadTree = useAction(knowledgeTree);
  const loadPage = useAction(knowledgePage);
  const frameRef = useRef<HTMLDivElement>(null);

  const [treeData] = useAtom(knowledgeTree.dataAtom);
  const [pageData] = useAtom(knowledgePage.dataAtom);

  const tree = treeData?.tree;

  const slug = useMemo(() => {
    return slugPath.split("/").filter(Boolean).pop() ?? null;
  }, [slugPath]);

  const currentSlugPath = location.pathname.replace(basePath, "");
  const activeSectionSlug = currentSlugPath.split("/")[0];

  const itemsToc = useMemo(
    () => getKBTocItems(pageData?.content ?? ""),
    [pageData?.content],
  );

  const articleDescription = useMemo(() => {
    if (!pageData?.content) return "";
    return stripMarkdown(pageData.content).slice(0, 160);
  }, [pageData?.content]);

  useEffect(() => {
    if (!tree) loadTree();
  }, [tree, loadTree]);

  useEffect(() => {
    if (slug) loadPage(slug);
  }, [slug, loadPage]);

  const firstDocPath = useMemo(() => {
    if (!tree) return null;
    const firstSection = tree[0];
    const firstDoc = firstSection?.docs?.[0];
    if (!firstDoc) return null;

    return `${basePath}${firstSection.slug}/${firstDoc.slug}`;
  }, [tree, basePath]);

  useEffect(() => {
    if (!firstDocPath || pageData !== null) return;
    navigate(firstDocPath, { replace: true });
  }, [firstDocPath, pageData, navigate]);

  if (!tree) {
    return null;
  }
  return (
    <Flex direction={"column"} alignItems={"center"} className={styles.page}>
      <Header
        actionsMobile={
          <div>
            <KnowledgeSidebar
              basePath={basePath}
              tree={tree}
              size="xl"
              currentSlugPath={currentSlugPath}
              activeSection={activeSectionSlug}
            />
          </div>
        }
        actions={
          <>
            <KnowledgeDropdownToc
              activeId={activeId}
              items={itemsToc}
              size="l"
            />
            <Link to={RoutesPaths.LK.BASE_PATH}>
              <Button size="xl" view="action" className={styles.button}>
                <Text variant="subheader-1">
                  <T
                    keyName="landing_page.header.login_button"
                    defaultValue="Начать работать"
                  />
                </Text>
              </Button>
            </Link>
          </>
        }
      />
      {pageData && (
        <PageMeta
          title={`${pageData.title} — База знаний | ${appConfig.displayName}`}
          description={articleDescription}
          path={location.pathname}
        />
      )}
      <div className={styles.contentDefault}>
        <div className={cn(styles.layoutFrame, styles.layoutFrameBase)}>
          <div className={styles.layoutMenu}>
            <div className={styles.layoutMenuPosition}>
              <KnowledgeSidebar
                basePath={basePath}
                tree={tree}
                size="xl"
                currentSlugPath={currentSlugPath}
                activeSection={activeSectionSlug}
              />
            </div>
          </div>
          <div className={styles.frame}>
            {pageData && (
              <>
                <div className={styles.content} ref={frameRef}>
                  <KnowledgeMarkdown
                    framerRef={frameRef}
                    basePath={basePath}
                    content={pageData.content}
                    onActiveChange={setActiveId}
                    route={route}
                  />
                </div>
                <div className={styles.tocPc}>
                  <KnowledgeToc
                    items={itemsToc}
                    activeId={activeId}
                    route={route}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </Flex>
  );
};
